import {
  trackCoreJourneyEvent,
  type CoreJourneyEventContext,
} from './coreJourneyAnalytics';

export const PLANNING_UPDATES_ANALYTICS_SOURCE = 'planning_updates_page';

export type PlanningUpdatesEventContext = Omit<CoreJourneyEventContext, 'source'> & {
  source?: string | null;
};

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/i;
const IPV4_PATTERN = /\b(?:\d{1,3}\.){3}\d{1,3}\b/;
const RAW_TEXT_METADATA_KEY_PATTERN =
  /(^|_)(raw_query|query_text|search_text|input_value|input_text|typed_value|typed_text|email|ip|visitor_id|user_id)($|_)/i;

export function analyticsCount(value: unknown): number {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.round(numeric));
}

export function countBucket(value: unknown): string {
  const count = analyticsCount(value);
  if (count === 0) return '0';
  if (count <= 3) return '1_3';
  if (count <= 10) return '4_10';
  if (count <= 50) return '11_50';
  if (count <= 250) return '51_250';
  return '251_plus';
}

export function textLengthBucket(value: string | null | undefined): string {
  const length = String(value || '').trim().length;
  if (length === 0) return '0';
  if (length <= 2) return '1_2';
  if (length <= 5) return '3_5';
  if (length <= 12) return '6_12';
  if (length <= 30) return '13_30';
  return '31_plus';
}

export function radiusBucket(value: unknown): string {
  const radius = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(radius) || radius <= 0) return 'exact_0';
  if (radius < 1) return 'under_1';
  if (radius <= 3) return '1_3';
  if (radius <= 10) return '3_10';
  return '10_plus';
}

function isUnsafeString(value: string): boolean {
  return EMAIL_PATTERN.test(value) || IPV4_PATTERN.test(value);
}

function cleanStringMetadata(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed || isUnsafeString(trimmed)) return undefined;
  return trimmed
    .toLowerCase()
    .replace(/https?:\/\/[^/\s]+/g, '')
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || undefined;
}

export function cleanPlanningUpdatesMetadata(
  metadata: Record<string, unknown> = {},
): Record<string, string | number | boolean> {
  const cleaned: Record<string, string | number | boolean> = {};

  for (const [key, value] of Object.entries(metadata)) {
    const cleanKey = cleanStringMetadata(key);
    if (!cleanKey) continue;
    if (RAW_TEXT_METADATA_KEY_PATTERN.test(cleanKey)) continue;

    if (typeof value === 'number') {
      if (Number.isFinite(value)) cleaned[cleanKey] = value;
      continue;
    }

    if (typeof value === 'boolean') {
      cleaned[cleanKey] = value;
      continue;
    }

    if (typeof value === 'string') {
      const cleanValue = cleanStringMetadata(value);
      if (cleanValue !== undefined) cleaned[cleanKey] = cleanValue;
      continue;
    }
  }

  return cleaned;
}

export function trackPlanningUpdatesEvent(
  action: string,
  context: PlanningUpdatesEventContext = {},
): void {
  trackCoreJourneyEvent('planning_research', action, {
    ...context,
    source: context.source || PLANNING_UPDATES_ANALYTICS_SOURCE,
    metadata: {
      page_type: 'planning_updates',
      ...cleanPlanningUpdatesMetadata(context.metadata),
    },
  });
}
